import { createUnifiedTheme, palettes } from '@backstage/theme';

/**
 * SA_SRE Custom Theme
 *
 * ui-design:
 *   Refactoring UI 準拠の 3層カラーパレット（Primary / Neutrals / Supporting）
 *   Primary: Blue (色相 205) #2680C2
 *   Neutrals: Blue Grey
 *   Supporting: Red Vivid / Yellow Vivid / Green / Cyan
 *
 * Color palette reference: .agent/skills/ui-design/references/color-system.md
 */
export const saSreLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: '#2680C2',    // Blue 400 — ブランドプライマリ
      light: '#62B0E8',   // Blue 600
      dark: '#0A558C',    // Blue 100
    },
    secondary: {
      main: '#4C63B6',    // Indigo 400
      light: '#7B93DB',   // Indigo 600
      dark: '#2D3A8C',    // Indigo 100
    },
    error: {
      main: '#E12D39',    // Red Vivid 400 — Critical / 障害
      light: '#F86A6A',   // Red Vivid 600
      dark: '#8A041A',    // Red Vivid 100
    },
    warning: {
      main: '#F0B429',    // Yellow Vivid 400 — Warning / 注意
      light: '#FADB5F',   // Yellow Vivid 600
      dark: '#B44D12',    // Yellow Vivid 100
    },
    success: {
      main: '#57AE5B',    // Green 500 — Success / 正常
      light: '#7BC47F',   // Green 600
      dark: '#0E5814',    // Green 100
    },
    info: {
      main: '#38BEC9',    // Cyan 500 — Info / 情報
      light: '#87EAF2',   // Cyan 700
      dark: '#0A6C74',    // Cyan 100
    },
    navigation: {
      background: '#102A43',  // Blue Grey 50 — ダークナビゲーション
      color: '#9FB3C8',       // Blue Grey 600
      indicator: '#2680C2',   // Blue 400
      selectedColor: '#ffffff',
      submenu: {
        background: '#243B53', // Blue Grey 100
      },
    },
    background: {
      default: '#F0F4F8',  // Blue Grey 900 — ページ背景
      paper: '#ffffff',
    },
    text: {
      primary: '#102A43',   // Blue Grey 50 — Primary テキスト
      secondary: '#627D98', // Blue Grey 400 — Secondary テキスト
    },
  },
  fontFamily: "'Noto Sans JP', 'Inter', 'Hiragino Sans', 'Yu Gothic UI', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  defaultPageTheme: 'home',
  pageTheme: {
    home: {
      colors: ['#2680C2', '#102A43'],  // Blue 400 → Blue Grey 50
      shape: 'wave',
      backgroundImage: '',
    },
    tool: {
      colors: ['#2680C2', '#4C63B6'],  // Blue 400 → Indigo 400
      shape: 'round',
      backgroundImage: '',
    },
    service: {
      colors: ['#57AE5B', '#207227'],  // Green 500 → Green 200
      shape: 'wave',
      backgroundImage: '',
    },
    documentation: {
      colors: ['#38BEC9', '#2680C2'],  // Cyan 500 → Blue 400
      shape: 'wave',
      backgroundImage: '',
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
            WebkitBoxShadow: '0 0 0 100px rgba(10,21,40,0.95) inset !important',
            WebkitTextFillColor: '#FFFFFF !important',
            caretColor: '#FFFFFF !important',
            transition: 'background-color 5000s ease-in-out 0s !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-shrink': {
            color: '#BCCCDC !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.875rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.8rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 600,
          color: '#102A43',  // Blue Grey 50
          fontSize: '0.875rem',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 2,
        },
      },
    },
    MuiCollapse: {
      defaultProps: {
        timeout: 300, // Doherty Threshold: < 400ms で知覚的に快適
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @keyframes sa-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes sa-pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes sa-slide-in {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `,
    },
  },
});

export const saSreDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: '#62B0E8',    // Blue 600 — ダークモード用明るめ
      light: '#B6E0FE',   // Blue 800
      dark: '#2680C2',    // Blue 400
    },
    secondary: {
      main: '#7B93DB',    // Indigo 600
    },
    error: {
      main: '#F86A6A',    // Red Vivid 600
    },
    warning: {
      main: '#FADB5F',    // Yellow Vivid 600
    },
    success: {
      main: '#7BC47F',    // Green 600
    },
    info: {
      main: '#87EAF2',    // Cyan 700
    },
    navigation: {
      background: '#0d1b2a',
      color: '#9FB3C8',     // Blue Grey 600
      indicator: '#62B0E8', // Blue 600
      selectedColor: '#ffffff',
      submenu: {
        background: '#112240',
      },
    },
  },
  fontFamily: "'Noto Sans JP', 'Inter', 'Hiragino Sans', 'Yu Gothic UI', system-ui, -apple-system, sans-serif",
  defaultPageTheme: 'home',
});
